import React from 'react';
import {
  Box,
  HStack,
  List,
  ListIcon,
  ListItem,
  Progress,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiCheck, FiCircle } from 'react-icons/fi';
import { passwordRequirements } from '@grcs/utils/auth/staffPasswordPolicy';

/** This measures completed requirements, not estimated password strength. */
export const PasswordRequirements = ({
  password,
  minimumLength,
  requireComplexity = false,
}: {
  password: string;
  minimumLength?: number;
  requireComplexity?: boolean;
}) => {
  const rules = passwordRequirements(
    password,
    minimumLength,
    requireComplexity,
  );
  const completed = rules.filter((rule) => rule.isValid).length;
  const muted = useColorModeValue('gray.600', 'gray.300');
  const success = useColorModeValue('green.700', 'green.300');
  const background = useColorModeValue('gray.50', 'gray.900');
  return (
    <Box bg={background} borderRadius="md" p={4}>
      <HStack justify="space-between" mb={2}>
        <Text fontSize="xs" fontWeight="semibold">
          Password requirements
        </Text>
        <Text fontSize="xs" color={muted}>
          {completed} of {rules.length} met
        </Text>
      </HStack>
      <Progress
        value={(completed / rules.length) * 100}
        size="xs"
        colorScheme={completed === rules.length ? 'green' : 'blue'}
        borderRadius="full"
        aria-label="Password requirements met"
        mb={3}
      />
      <List
        display="grid"
        gridTemplateColumns={{ base: '1fr', sm: '1fr 1fr' }}
        gap={2}
      >
        {rules.map((rule) => (
          <ListItem
            key={rule.label}
            fontSize="xs"
            color={rule.isValid ? success : muted}
          >
            <ListIcon
              as={rule.isValid ? FiCheck : FiCircle}
              aria-hidden="true"
            />
            {rule.label}
            <Box as="span" srOnly>
              {rule.isValid ? ' — met' : ' — not yet met'}
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
